import { trainingBillingRecords } from '../data/trainingBilling'
import { practicesStore } from './practicesStore'
import { practiceTotal } from '../utils/practices'
import { Practice } from '../types/practice'
import {
  InvoiceDocument,
  ProformaDocument,
  TrainingBillingRecord,
  TrainingBillingStatus,
} from '../types/trainingBilling'

type Listener = () => void

const today = () => new Date().toISOString().slice(0, 10)
const PAID: TrainingBillingStatus = 'Fattura pagata'

// Billing positions of Formazione › Fatturazione. Records are generated
// automatically when a practice is marked "Proforma da fare" or "Fattura da fare".
class TrainingBillingStore {
  private records: TrainingBillingRecord[] = [...trainingBillingRecords]
  private listeners = new Set<Listener>()

  constructor() {
    this.sync(practicesStore.getAll())
    practicesStore.subscribe(() => {
      if (this.sync(practicesStore.getAll())) this.emit()
    })
  }

  getAll(): TrainingBillingRecord[] {
    return this.records
  }

  get(practiceId: string): TrainingBillingRecord | undefined {
    return this.records.find((r) => r.practiceId === practiceId)
  }

  // Proforma already issued (with a number), to link them to an invoice
  issuedProformaNumbers(): string[] {
    return this.records
      .map((r) => r.proforma?.number)
      .filter((n): n is string => Boolean(n))
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit() {
    this.listeners.forEach((l) => l())
  }

  private nextProgressive(prefix: 'PF' | 'FT'): string {
    const year = new Date().getFullYear()
    const tag = `${prefix}-${year}-`
    const used = this.records
      .map((r) => (prefix === 'PF' ? r.proforma : r.invoice)?.progressive)
      .filter((p): p is string => Boolean(p?.startsWith(tag)))
      .map((p) => Number(p.slice(tag.length)))
    const next = (used.length ? Math.max(...used) : 0) + 1
    return `${tag}${String(next).padStart(3, '0')}`
  }

  // Aligns records with the practices; returns true when something changed
  private sync(practices: Practice[]): boolean {
    let changed = false
    const next: TrainingBillingRecord[] = []

    practices.forEach((p) => {
      const wantsProforma = p.proforma !== 'off'
      const wantsInvoice = p.invoice !== 'off'
      const current = this.records.find((r) => r.practiceId === p.id)
      if (!wantsProforma && !wantsInvoice) {
        if (current) changed = true
        return
      }
      let record: TrainingBillingRecord = current
        ? { ...current }
        : {
            practiceId: p.id,
            status: wantsProforma ? 'Proforma da fare' : 'Fattura da fare',
          }
      if (!current) changed = true

      if (wantsProforma && !record.proforma) {
        record.proforma = {
          progressive: this.nextProgressive('PF'),
          issueDate: today(),
        }
        changed = true
      }
      if (!wantsProforma && record.proforma && !record.proforma.number) {
        record.proforma = undefined
        changed = true
      }
      if (wantsInvoice && !record.invoice) {
        record.invoice = {
          progressive: this.nextProgressive('FT'),
          amount: practiceTotal(p),
          issueDate: today(),
          proformaNumber: record.proforma?.number,
        }
        // Invoice requested after the proforma was issued
        if (record.status === 'Proforma effettuata') record.status = 'Fattura da fare'
        changed = true
      }
      if (!wantsInvoice && record.invoice && !record.invoice.number) {
        record.invoice = undefined
        changed = true
      }
      // Keep the automatic status coherent with the documents still requested
      if (record.status === 'Proforma da fare' && !record.proforma && record.invoice)
        record.status = 'Fattura da fare'
      if (record.status === 'Fattura da fare' && !record.invoice && record.proforma)
        record.status = record.proforma.number ? 'Proforma effettuata' : 'Proforma da fare'

      if (!record.proforma && !record.invoice) {
        changed = true
        return
      }
      next.push(record)
      // Newly generated records go through nextProgressive on this.records
      this.records = [
        ...this.records.filter((r) => r.practiceId !== p.id),
        record,
      ]
    })

    // Drop records of deleted practices
    if (next.length !== this.records.length) changed = true
    this.records = next
    return changed
  }

  setStatus(practiceId: string, status: TrainingBillingStatus) {
    this.records = this.records.map((r) => {
      if (r.practiceId !== practiceId) return r
      const invoice = r.invoice && {
        ...r.invoice,
        // Payment date recorded automatically when the invoice becomes paid
        paymentDate:
          status === PAID
            ? r.status === PAID
              ? r.invoice.paymentDate
              : today()
            : undefined,
      }
      return { ...r, status, invoice }
    })
    this.emit()
  }

  saveProforma(practiceId: string, proforma: ProformaDocument) {
    const record = this.get(practiceId)
    if (!record) return
    const issued = Boolean(proforma.number)
    this.records = this.records.map((r) =>
      r.practiceId === practiceId
        ? {
            ...r,
            proforma,
            status:
              issued && r.status === 'Proforma da fare'
                ? r.invoice
                  ? 'Fattura da fare'
                  : 'Proforma effettuata'
                : r.status,
          }
        : r,
    )
    if (issued) practicesStore.update(practiceId, { proforma: 'fatto' })
    this.emit()
  }

  saveInvoice(practiceId: string, invoice: InvoiceDocument) {
    const record = this.get(practiceId)
    if (!record) return
    const issued = Boolean(invoice.number)
    this.records = this.records.map((r) =>
      r.practiceId === practiceId
        ? {
            ...r,
            invoice,
            status:
              issued &&
              (r.status === 'Fattura da fare' ||
                r.status === 'Proforma da fare' ||
                r.status === 'Proforma effettuata')
                ? 'Fattura effettuata'
                : r.status,
          }
        : r,
    )
    if (issued) practicesStore.update(practiceId, { invoice: 'fatto' })
    this.emit()
  }
}

export const trainingBillingStore = new TrainingBillingStore()
